import PropTypes from 'prop-types';
// CORE
import React, { useState, useMemo } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// Data
import { actionHelpers } from 'app-components/flux/core';
import { rootUiActions } from 'app-components/flux/root-ui';
import { useDispatch, useSelector } from 'react-redux';

// Custom
import CoffeeSpill from 'assets/illustrations/CoffeeSpill.png';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import LoadingView from 'components/LoadingView';
import ZeroStateView from 'components/ZeroStateView';
import useTxnActions from 'data/transactionActions/useTxnActions';

import { DIALOG_TYPE } from './actions';

const useStyles = makeStyles()(() => ({
  finalImage: {
    marginBottom: -20,
    width: 200,
  },
}));

const DeleteReminderDialog = (props) => {
  const { classes } = useStyles();

  const { txn, wrapperId } = props;
  const dispatch = useDispatch();

  const [tabIndex, setTabIndex] = useState(0);
  const [deletePreviousInstances, setDeletePreviousInstances] = useState(false);

  const { performTransactionAction, deleteTransactions } = useTxnActions({ wrapperId });

  const model = useSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn.stModelId),
  );
  const scheduledTxnType = model && scheduledTransactionsUtils.typeFromScheduledTransaction(model)?.toLowerCase();

  const categoryName = useSelector((state) => categoriesSelectors.getCategoryById(state, txn?.coa?.id))?.name;

  const source =
    window.location.pathname === '/'
      ? 'dashboard'
      : window.location.pathname.replaceAll?.(/[/-]|(\d+)/g, (match) =>
          match === '/' ? '' : match === '-' ? ' ' : 'xxx',
        );

  const txnsByAccountId = useSelector(transactionsSelectors.getTransactionsByAccountId);

  const numPreviousInstances = useMemo(() => {
    if (model && txnsByAccountId) {
      return txnsByAccountId
        .get(model.transaction.accountId)
        .filter(
          (t) => t.stModelId === model.id && t.source === 'SCHEDULED_TRANSACTION_PENDING' && t.stDueOn < txn.stDueOn,
        ).size;
    }
    return 0;
  }, [model, txn, txnsByAccountId]);

  const onClose = () => {
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE));
  };

  // data stuff
  const actOnScheduledTxn = (action) => {
    const actionObj = {
      id: txn.id,
      action,
      overdueAction: action,
      instanceDate: txn.stDueOn,
    };
    performTransactionAction(actionObj);
  };

  // If the instance is the next instance or deletePreviousInstances is set, then we need to skip it (so model advances).
  // Otherwise, we can delete it.
  //   Note: When deleting instance, it will be revived if model changes.
  //
  const deleteInstance = () => {
    if (txn.stDueOn === model?.dueOn || deletePreviousInstances) {
      actOnScheduledTxn('skip');
    } else {
      deleteTransactions([txn]);
    }
    tracker.track(tracker.events.deleteReminder, {
      type: scheduledTxnType || 'other',
      source: source,
      category: categoryName || 'other',
    });
    onClose();
  };

  const deleteModel = () => {
    if (!model) {
      actOnScheduledTxn('skip');
    } else {
      setTabIndex(-1);
      const payload = scheduledTransactionsTypes.mkScheduledTransactionPatch({
        id: txn.stModelId,
        ...model.toJS(),
        isUserVerified: true,
      });
      actionHelpers
        .bindPromiseAction(
          dispatch,
          scheduledTransactionsActions.deleteScheduledTransaction,
        )(payload)
        .then((_resp) => {
          setTabIndex(1);
          setTimeout(() => onClose(), 800);
        })
        .catch((_error) => {
          setTabIndex(-2);
        });
    }
  };

  let dialogTitle = 'Delete Reminder';
  if (tabIndex === -1 || tabIndex === 1) dialogTitle = '';
  if (tabIndex === -2) dialogTitle = 'Deletion Failed';

  return (
    <StdDialog
      aria-labelledby='survey'
      onClose={onClose}
      open
      sharedcomponentid={'DELETE_REMINDER_DIALOG'}
      size={tabIndex === 0 || tabIndex === -2 ? 'sm' : 'xs'}
      title={dialogTitle}
      disableBackdropClick={tabIndex === -1}
      showCloseButton={tabIndex !== -1 && tabIndex !== 1}
    >
      {tabIndex === 0 && (
        <>
          <StdDialogContent id='delete_reminder_dialog'>
            <Typography variant='titleRegular'>{`Are you sure you want to delete the reminder for "${txn.payee}"?`}</Typography>
            {numPreviousInstances > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deletePreviousInstances}
                    onChange={() => setDeletePreviousInstances(!deletePreviousInstances)}
                  />
                }
                label={`Also, delete ${numPreviousInstances} previous unmatched reminder${numPreviousInstances > 1 ? 's' : ''}`}
              />
            )}
          </StdDialogContent>
          <StdDialogActions
            primary={{
              children: 'Delete',
              color: 'error',
              variant: 'outlined',
              onClick: deleteInstance,
            }}
            secondary={{
              onClick: onClose,
            }}
            tertiaryAction={
              model && {
                children: 'Delete Series',
                color: 'error',
                onClick: deleteModel,
              }
            }
          />
        </>
      )}
      {tabIndex === 1 && (
        <Box height={350} display='flex' alignItems='center' flexDirection='column' justifyContent='center'>
          <img src={CoffeeSpill} alt='coffee-spill' className={classes.finalImage} />
          <Typography variant='headlineRegular'>Deleted!</Typography>
        </Box>
      )}
      {tabIndex === -1 && (
        <Box height={350} display='flex' alignItems='center' flexDirection='column' justifyContent='center'>
          <LoadingView size={100} />
        </Box>
      )}
      {tabIndex === -2 && (
        <ZeroStateView
          id='failed-to-fetch'
          title='Failed to delete series'
          description='We were unable to delete this series. Please try again later.'
          qIllustration='planeCrash'
          size='small'
        />
      )}
    </StdDialog>
  );
};

DeleteReminderDialog.defaultProps = {
  txn: {},
};

DeleteReminderDialog.propTypes = {
  txn: PropTypes.object,
  wrapperId: PropTypes.string,
};

export default DeleteReminderDialog;
